import { useMutation, useQueryClient } from 'react-query';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useMemo } from 'react';
import axiosClient from '../../../../lib/axios';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { IDataMensagens } from '../../../../types/api/geral/mensagens';
import { TOpenModalMensagens } from '..';

export default function DialogCriacaoEditMensagem({
    defaultValue,
    open,
    handleClose
}: DialogCriacaoEditMensagemProps) {

    const queryClient = useQueryClient();

    //Valores iniciais
    const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
    const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue.defaultValue : null, [defaultValue])
    //

    const methods = useForm<IDataMensagens>({
        defaultValues: {
            id: valoresIniciais?.id,
            canal: valoresIniciais?.canal,
            mensagem: valoresIniciais?.mensagem || '',
            cooperativa_id: valoresIniciais?.cooperativa_id || 0,
            programa: valoresIniciais?.programa || 'Financeiro',
        }
    })
    const { control, handleSubmit, getFieldState } = methods;

    //Querys e mutations
    const { mutate: criarMensagem, isLoading: isLoadingCriarMsg } = useMutation({
        mutationKey: 'cadastrar-mensagem',
        mutationFn: (inputs: any) => axiosClient.post("cadastrar-mensagem", inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao cadastrar mensagem!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-mensagens"])
            handleClose()
            enqueueSnackbar('Cadastro de mensagem realizado com sucesso!', { variant: 'success' });
        }
    })

    const { mutate: atualizarMensagem, isLoading: isLoadingAttMsg } = useMutation({
        mutationKey: 'atualizar-mensagem',
        mutationFn: (inputs: any) => axiosClient.put(`atualizar-mensagem/${valoresIniciais?.id}`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao atualizar função!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-mensagens"])
            handleClose()
            enqueueSnackbar('Atualização de função com sucesso!', { variant: 'success' });
        }
    })
    //

    //Funções manipuladoras
    const onSubmit = (data: IDataMensagens) => {
        const params = {
            mensagem: data.mensagem,
            id: data.id,
            canal: data.canal,
            programa: data.programa,
        }
        if (isEdit) {
            atualizarMensagem(params)
        }
        if (!isEdit) {
            criarMensagem(params)
        }
    }
    //

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {`${!isEdit ? "Adicionar" : "Editar"} mensagem`}
                </DialogTitle>

                {
                    (isLoadingAttMsg || isLoadingCriarMsg) &&
                    <Box display="flex" justifyContent="center" mt={4} mb={8}>
                        <CircularProgress />
                    </Box>
                }

                {
                    !isLoadingAttMsg && !isLoadingCriarMsg &&
                    <>
                        <Box
                            component={'form'}
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <DialogContent>
                                <Grid container spacing={2}>
                                    <Grid size={{xs: 6}}>
                                        <Controller
                                            name="programa"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field, fieldState: { error, invalid } }) => (
                                                <FormControl required fullWidth variant="outlined" error={!!error}>
                                                    <InputLabel id="programa-label">Programa</InputLabel>
                                                    <Select
                                                        {...field}
                                                        labelId="programa-label"
                                                        label="Programa"
                                                    >
                                                        {programas.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    <FormHelperText>{error?.message}</FormHelperText>
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{xs: 6}}>
                                        <Controller
                                            name="canal"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field, fieldState: { error, invalid } }) => (
                                                <FormControl required fullWidth variant="outlined" error={!!error}>
                                                    <InputLabel id="canal-label">Canal</InputLabel>
                                                    <Select
                                                        {...field}
                                                        labelId="canal-label"
                                                        label="canal"
                                                    >
                                                        {canais.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    <FormHelperText>{error?.message}</FormHelperText>
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{xs: 12}}>
                                        <Controller
                                            name="mensagem"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    required
                                                    defaultValue={valoresIniciais?.mensagem}
                                                    label="Mensagem"
                                                    multiline
                                                    minRows={4}
                                                    variant="outlined"
                                                    fullWidth
                                                    error={getFieldState('mensagem').invalid}
                                                    helperText={getFieldState('mensagem').error?.message || ""}
                                                    onChange={(e) => {
                                                        const formattedValue = e.target.value.replace(/\r?\n/g, '\n');
                                                        field.onChange(formattedValue);
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                </Grid>
                                <Box mt={2} width={'100%'} display={'flex'} justifyContent={'flex-end'} alignItems={'center'} gap={2}>
                                    <Button
                                        onClick={handleClose}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        autoFocus
                                        disabled={isLoadingCriarMsg || isLoadingAttMsg}

                                    >
                                        {
                                            (isLoadingCriarMsg || isLoadingAttMsg) &&
                                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                                        }
                                        {!isEdit ? "Adicionar" : "Atualizar"}
                                    </Button>
                                </Box>
                            </DialogContent>
                        </Box>
                    </>
                }
            </Dialog>
        </FormProvider>
    )
}

interface DialogCriacaoEditMensagemProps {
    defaultValue: TOpenModalMensagens
    open: boolean;
    handleClose: () => void;
}

const programas = [
    { value: 'Financeiro', label: 'Financeiro' },
    // { value: 'Comercial', label: 'Comercial' },
    // { value: 'Estoque', label: 'Estoque' },
]

const canais = [
    { value: 'WhatsApp', label: 'WhatsApp' },
    { value: 'Email', label: 'E-mail' },
]
